import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { resolveEventAttendanceFlags } from '@/utils/admin/eventsAttendance';
import { AttendeeListItemProps } from './types';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { GlassStatusBadge } from '@/components/admin/redesign';
import { SVG_COMPONENTS } from '@/assets/admin';

export const AttendeeListItem: React.FC<AttendeeListItemProps> = ({
  resident,
  onPress,
  eventId,
}) => {
  const { redesign, semantic } = useAdminColors();

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Prefer EventResident for current event; fallback to events[0].rsvpList if present
  const { checkedIn: isCheckedIn, hasRsvp } = resolveEventAttendanceFlags(
    resident,
    eventId,
  );

  const accessibilityLabel = `${resident.firstName} ${resident.lastName}, ${resident.email}${
    resident.unitNumberSpace ? `, Unit ${resident.unitNumberSpace}` : ''
  }${hasRsvp ? ', RSVP confirmed' : ', No RSVP'}${
    isCheckedIn ? ', Checked in' : ', Not checked in'
  }`;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint="Tap to view resident details and check-in options"
      accessibilityRole="button"
    >
      <GlassCard padding={16} borderRadius={12} style={styles.cardSpacing}>
        <View style={styles.row}>
          <View
            style={[
              styles.avatarContainer,
              { backgroundColor: redesign.accent.blueBgLight },
            ]}
          >
            <Text style={[styles.avatarText, { color: semantic.text.link }]}>
              {getInitials(resident.firstName, resident.lastName)}
            </Text>
            {isCheckedIn && (
              <View style={styles.checkedInBadge}>
                <SVG_COMPONENTS.CHECK_CIRCLE
                  width={20}
                  height={20}
                  color={semantic.status.success}
                />
              </View>
            )}
          </View>
          <View style={styles.content}>
            <Text style={[styles.name, { color: redesign.text.primary }]}>
              {resident.firstName} {resident.lastName}
            </Text>
            <Text style={[styles.email, { color: redesign.text.muted }]}>
              {resident.email}
            </Text>
            <View style={styles.footer}>
              {resident.unitNumberSpace && (
                <View
                  style={[
                    styles.unitBadge,
                    { backgroundColor: redesign.glass.buttonBg },
                  ]}
                >
                  <Text
                    style={[styles.unitBadgeText, { color: redesign.text.subtle }]}
                  >
                    Unit {resident.unitNumberSpace}
                  </Text>
                </View>
              )}
            </View>
          </View>
          <View style={styles.checkInStatus}>
            {isCheckedIn ? (
              <View style={styles.checkedInStatus}>
                <View
                  style={[
                    styles.statusDot,
                    { backgroundColor: semantic.status.success },
                  ]}
                />
                <Text
                  style={[styles.statusLabel, { color: semantic.status.success }]}
                >
                  Checked In
                </Text>
              </View>
            ) : (
              <View style={styles.checkedInStatus}>
                <View
                  style={[
                    styles.statusDot,
                    { backgroundColor: redesign.text.subtle },
                  ]}
                />
                <Text style={[styles.statusLabel, { color: redesign.text.subtle }]}>
                  Not Checked In
                </Text>
              </View>
            )}
            {hasRsvp && (
              <View style={styles.rsvpBadgeWrap}>
                <GlassStatusBadge label="RSVP'd" variant="success" />
              </View>
            )}
          </View>
        </View>
      </GlassCard>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cardSpacing: {
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    position: 'relative',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
  },
  checkedInBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
  },
  content: {
    flex: 1,
  },
  name: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  email: {
    fontSize: 14,
    marginBottom: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  unitBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  unitBadgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  checkInStatus: {
    alignItems: 'center',
  },
  rsvpBadgeWrap: {
    marginTop: 8,
  },
  checkedInStatus: {
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginBottom: 4,
  },
  statusLabel: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
});
